import { useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, MessageCircle, Map as MapIcon } from "lucide-react";
import RoadmapTimeline, { RoadmapLegend } from "@/components/RoadmapTimeline";
import { overallProgress } from "@/lib/roadmapData";
import useRoadmap from "@/lib/useRoadmap";

export default function Roadmap() {
  const { t } = useTranslation();
  useEffect(() => {
    document.title = "Roadmap — Territoria Wiki";
  }, []);

  const { phases } = useRoadmap();
  const stats = overallProgress(phases);
  const totalFeatures = stats.totalFeatures;
  const totalDone = stats.shipped;
  const totalWip  = stats.inBuild;
  const overallPct = stats.pct;
  const phaseCount = stats.phaseCount;

  return (
    <div data-testid="roadmap-page" className="relative">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-[var(--t-border)]">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] md:w-[1000px] md:h-[1000px] rounded-full bg-[radial-gradient(circle,_rgba(58,160,255,0.18)_0%,_transparent_60%)]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 md:px-6 pt-10 md:pt-16 pb-8 md:pb-12">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-xs md:text-sm font-mono uppercase tracking-[0.2em] text-[var(--t-text-dim)] hover:text-[var(--t-cyan)] transition-colors mb-6"
            data-testid="roadmap-back-home"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> {t("roadmap.backHome")}
          </Link>

          <div className="inline-flex items-center gap-2 px-3 py-1 border border-[var(--t-border-glow)] mb-5">
            <span className="w-1.5 h-1.5 rounded-full bg-[var(--t-blue)] pulse-glow" />
            <span className="font-mono text-[10px] md:text-[11px] tracking-[0.3em] text-[var(--t-cyan)] uppercase">
              {t("roadmap.eyebrow")}
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mb-4 md:mb-5">
            <span className="block bg-gradient-to-r from-[var(--t-blue-glow)] via-[var(--t-cyan)] to-[var(--t-blue)] bg-clip-text text-transparent">
              {t("roadmap.title1")}
            </span>
            <span className="block text-[var(--t-text)]">{t("roadmap.title2")}</span>
          </h1>
          <p className="text-base md:text-lg text-[var(--t-text-dim)] max-w-2xl leading-relaxed mb-7 md:mb-9">
            {t("roadmap.subtitle")}
          </p>

          {/* Stat row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 max-w-3xl mb-7 md:mb-9">
            <BigStat label={t("roadmap.statPhases")}   value={String(phaseCount).padStart(2, "0")} accent="var(--t-cyan)" />
            <BigStat label={t("roadmap.statKeyAreas")} value={String(totalFeatures).padStart(2, "0")} accent="var(--t-blue-glow)" />
            <BigStat label={t("roadmap.statShipped")}  value={String(totalDone).padStart(2, "0")}     accent="#5fd99a" />
            <BigStat label={t("roadmap.statInBuild")}  value={String(totalWip).padStart(2, "0")}      accent="#f3c552" />
          </div>

          {/* Overall progress */}
          <div className="max-w-3xl mb-6 md:mb-8" data-testid="roadmap-overall-progress">
            <div className="flex items-center justify-between mb-2 font-mono text-[10px] md:text-[11px] uppercase tracking-[0.25em]">
              <span className="text-[var(--t-text-dim)]">{t("roadmap.overallProgress")}</span>
              <span className="text-[var(--t-cyan)]">{overallPct}%</span>
            </div>
            <div className="h-2 w-full bg-[var(--t-panel-2)] border border-[var(--t-border)] overflow-hidden">
              <div
                className="h-full transition-all duration-700"
                style={{
                  width: `${overallPct}%`,
                  background:
                    "linear-gradient(90deg, var(--t-blue-deep) 0%, var(--t-blue) 50%, var(--t-cyan) 100%)",
                  boxShadow: "0 0 14px rgba(102,230,255,0.5)",
                }}
              />
            </div>
          </div>

          <RoadmapLegend />
        </div>
      </section>

      {/* Timeline */}
      <section className="relative max-w-5xl mx-auto px-4 md:px-6 pt-10 md:pt-16 pb-16 md:pb-24">
        <RoadmapTimeline phases={phases} />
      </section>

      {/* Bottom CTA */}
      <section className="relative max-w-7xl mx-auto px-4 md:px-6 pb-16 md:pb-24" data-testid="roadmap-cta">
        <div className="panel panel-glow corner-frame p-6 md:p-9 lg:p-10 relative overflow-hidden">
          <div className="absolute -right-24 -top-24 w-80 h-80 rounded-full bg-[radial-gradient(circle,_rgba(88,101,242,0.30)_0%,_transparent_70%)] pointer-events-none" />
          <div className="relative grid md:grid-cols-[1.4fr_1fr] gap-7 md:gap-10 items-center">
            <div>
              <div className="font-mono text-[10px] md:text-[11px] tracking-[0.3em] uppercase text-[#9ca6ff] mb-2">
                {t("roadmap.ctaEyebrow")}
              </div>
              <h2 className="font-display text-2xl md:text-4xl text-[var(--t-text)] uppercase leading-tight">
                {t("roadmap.ctaTitle")}
              </h2>
              <p className="mt-3 md:mt-4 text-sm md:text-base text-[var(--t-text-dim)] max-w-xl leading-relaxed">
                {t("roadmap.ctaBlurb")}
              </p>
            </div>
            <div className="flex flex-col gap-3">
              <Link to="/discord" className="btn-primary justify-center" data-testid="roadmap-discord-cta">
                <MessageCircle className="w-4 h-4" /> {t("roadmap.ctaDiscord")}
              </Link>
              <Link to="/forum" className="btn-ghost justify-center" data-testid="roadmap-forum-cta">
                <MapIcon className="w-4 h-4" /> {t("roadmap.ctaForum")}
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function BigStat({ label, value, accent }) {
  return (
    <div className="border-l-2 pl-3 py-1" style={{ borderColor: accent }}>
      <div className="font-display text-2xl md:text-3xl" style={{ color: accent }}>
        {value}
      </div>
      <div className="font-mono text-[10px] md:text-[11px] tracking-[0.25em] uppercase text-[var(--t-text-muted)] mt-0.5">
        {label}
      </div>
    </div>
  );
}
